import { create } from "zustand";

import type { UploadedFile } from "../types";

interface FileUploadState {
  uploadedFiles: Record<string, UploadedFile[]>;
  uploadingFiles: Record<string, File[]>;
  // Actions
  addUploadedFile: (conversationKey: string, file: UploadedFile) => void;
  removeUploadedFile: (conversationKey: string, fileId: string) => void;
  addUploadingFile: (conversationKey: string, file: File) => void;
  removeUploadingFile: (conversationKey: string, file: File) => void;
  clearConversationFiles: (conversationKey: string) => void;
  // Getters
  getUploadedFiles: (conversationKey: string) => UploadedFile[];
  getUploadingFiles: (conversationKey: string) => File[];
}

export default create<FileUploadState>((set, get) => ({
  uploadedFiles: {},
  uploadingFiles: {},

  addUploadedFile: (conversationKey, file) => {
    if (!conversationKey) return;

    set((state) => ({
      uploadedFiles: {
        ...state.uploadedFiles,
        [conversationKey]: [
          ...(state.uploadedFiles[conversationKey] || []),
          file,
        ],
      },
    }));
  },
  removeUploadedFile: (conversationKey, fileId) => {
    if (!conversationKey) return;

    set((state) => ({
      uploadedFiles: {
        ...state.uploadedFiles,
        [conversationKey]: (state.uploadedFiles[conversationKey] || []).filter(
          (file) => file.id !== fileId,
        ),
      },
    }));
  },
  addUploadingFile: (conversationKey, file) => {
    if (!conversationKey) return;

    set((state) => ({
      uploadingFiles: {
        ...state.uploadingFiles,
        [conversationKey]: [
          ...(state.uploadingFiles[conversationKey] || []),
          file,
        ],
      },
    }));
  },
  removeUploadingFile: (conversationKey, file) => {
    if (!conversationKey) return;

    set((state) => ({
      uploadingFiles: {
        ...state.uploadingFiles,
        [conversationKey]: (state.uploadingFiles[conversationKey] || []).filter(
          (f) => f !== file,
        ),
      },
    }));
  },
  clearConversationFiles: (conversationKey) => {
    if (!conversationKey) return;

    set((state) => {
      const { [conversationKey]: removedUploaded, ...restUploaded } =
        state.uploadedFiles;
      const { [conversationKey]: removedUploading, ...restUploading } =
        state.uploadingFiles;

      return {
        uploadedFiles: restUploaded,
        uploadingFiles: restUploading,
      };
    });
  },
  getUploadedFiles: (conversationKey) =>
    get().uploadedFiles[conversationKey] || [],
  getUploadingFiles: (conversationKey) =>
    get().uploadingFiles[conversationKey] || [],
}));
